import { useState, useEffect, useRef, useCallback } from "react";
import maplibregl from "maplibre-gl";
import { PHILIPPINES_CENTER, PHILIPPINES_BOUNDS, COLORS } from "../components/constants";

const CARTO_BASEMAP_KEY = process.env.REACT_APP_CARTO_BASEMAP_KEY;
const CARTO_VOYAGER_TILE_URL =
  `https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${encodeURIComponent(CARTO_BASEMAP_KEY || "")}`;

// 1. STANDARD PIN (Teal)
const TEARDROP_SVG = `
<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:${COLORS.primary};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${COLORS.primaryDark};stop-opacity:1" />
    </linearGradient>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
    </filter>
  </defs>
  <path d="M30 2 C16.7 2 6 12.7 6 26 C6 39.3 30 58 30 58 C30 58 54 39.3 54 26 C54 12.7 43.3 2 30 2 Z" 
        fill="url(#grad1)" filter="url(#shadow)" stroke="white" stroke-width="2"/>
  <circle cx="30" cy="26" r="14" fill="white"/>
</svg>
`;

// 2. ACTIVE PIN (Brand Green #85bc40)
const TEARDROP_ACTIVE_SVG = `
<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="gradActive" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#85bc40;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#6da632;stop-opacity:1" />
    </linearGradient>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
    </filter>
  </defs>
  <path d="M30 2 C16.7 2 6 12.7 6 26 C6 39.3 30 58 30 58 C30 58 54 39.3 54 26 C54 12.7 43.3 2 30 2 Z" 
        fill="url(#gradActive)" filter="url(#shadow)" stroke="white" stroke-width="2"/>
  <circle cx="30" cy="26" r="14" fill="white"/>
  <circle cx="30" cy="26" r="6" fill="#85bc40"/>
</svg>
`;

const createCircleGeoJSON = (center, radiusKm) => {
  if (!center) return null;
  const points = 64;
  const coordinates = [];
  const lon = parseFloat(center[0]);
  const lat = parseFloat(center[1]);
  const r = parseFloat(radiusKm);
  const distanceX = r / (111.32 * Math.cos((lat * Math.PI) / 180));
  const distanceY = r / 110.574;

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    coordinates.push([lon + x, lat + y]);
  }
  coordinates.push(coordinates[0]);
  return { type: "Feature", geometry: { type: "Polygon", coordinates: [coordinates] } };
};

export const useStationMap = ({
  stations,
  searchedLocation,
  radius,
  selectedStation,
  setSelectedStation,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popupRef = useRef(null);
  const userLocationMarker = useRef(null);
  
  const [mapLoaded, setMapLoaded] = useState(false);
  const [popupStation, setPopupStation] = useState(null);
  const [popupContainer, setPopupContainer] = useState(null);

  // --- INITIALIZE MAP ---
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          "carto-voyager": { 
            type: "raster",
            tiles: [CARTO_VOYAGER_TILE_URL],
            tileSize: 256,
            attribution: '&copy; OSM &copy; CARTO',
          },
        },
        layers: [
          {
            id: "carto-voyager-layer",
            type: "raster",
            source: "carto-voyager",
            minzoom: 0,
            maxzoom: 19,
          },
        ],
      },
      center: PHILIPPINES_CENTER,
      zoom: 6,
      maxBounds: PHILIPPINES_BOUNDS,
      fadeDuration: 0,
    });

    map.current.addControl(new maplibregl.NavigationControl(), "top-right");

    map.current.on("load", () => {
      const img1 = new Image(60, 60);
      img1.onload = () => {
        if (!map.current) return;
        map.current.addImage('custom-teardrop', img1);
      };
      img1.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(TEARDROP_SVG);

      const img2 = new Image(60, 60);
      img2.onload = () => {
        if (!map.current) return;
        map.current.addImage('custom-teardrop-active', img2);
        
        initializeLayers();
        setMapLoaded(true);
      };
      img2.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(TEARDROP_ACTIVE_SVG);
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // --- LAYERS SETUP ---
  const initializeLayers = () => {
    if (!map.current) return;

    map.current.addSource("radius-overlay", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    map.current.addLayer({
      id: "radius-fill",
      type: "fill",
      source: "radius-overlay",
      paint: { "fill-color": COLORS.primary, "fill-opacity": 0.1 },
    });
    map.current.addLayer({
      id: "radius-line",
      type: "line",
      source: "radius-overlay",
      paint: { "line-color": COLORS.primary, "line-width": 2, "line-dasharray": [4, 4] },
    });

    map.current.addSource("stations", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      cluster: true,
      clusterMaxZoom: 13,
      clusterRadius: 50,
    });

    map.current.addLayer({
      id: "clusters",
      type: "circle",
      source: "stations",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": [
          "step", ["get", "point_count"],
          COLORS.primary, 10,
          COLORS.primaryDark, 50,
          COLORS.accent 
        ],
        "circle-radius": ["step", ["get", "point_count"], 18, 10, 24, 50, 30],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff"
      },
    });

    map.current.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "stations",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 13,
      },
      paint: { "text-color": "#ffffff" },
    });

    map.current.addLayer({
      id: "unclustered-point",
      type: "symbol",
      source: "stations",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": "custom-teardrop",
        "icon-size": 0.7, 
        "icon-allow-overlap": true,
        "icon-anchor": "bottom",
      },
    });

    setupEvents();
  };

  const setupEvents = () => {
    map.current.on("click", "clusters", async (e) => {
      const features = map.current.queryRenderedFeatures(e.point, { layers: ["clusters"] });
      const clusterId = features[0].properties.cluster_id;
      const zoom = await map.current.getSource("stations").getClusterExpansionZoom(clusterId);
      map.current.easeTo({ center: features[0].geometry.coordinates, zoom: zoom });
    });

    map.current.on("click", "unclustered-point", (e) => {
      e.preventDefault(); 
      const props = e.features[0].properties; 
      
      const stationData = { ...props };
      if (typeof stationData.gallery === 'string') {
          try {
              stationData.gallery = JSON.parse(stationData.gallery);
          } catch (err) {
              console.warn("Failed to parse gallery from map:", err);
              stationData.gallery = [];
          }
      }
      setSelectedStation(stationData); 
    });

    map.current.on("click", (e) => {
      const features = map.current.queryRenderedFeatures(e.point, { layers: ["unclustered-point", "clusters"] });
      if (!features.length) {
        setSelectedStation(null);
      }
    });

    const setPointer = () => map.current.getCanvas().style.cursor = "pointer";
    const setGrab = () => map.current.getCanvas().style.cursor = "";
    map.current.on("mouseenter", "clusters", setPointer);
    map.current.on("mouseleave", "clusters", setGrab);
    map.current.on("mouseenter", "unclustered-point", setPointer);
    map.current.on("mouseleave", "unclustered-point", setGrab);
  };

  // --- VISIBILITY CHECK ---
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const checkVisibility = () => {
        if (!selectedStation) return;
        const currentZoom = map.current.getZoom();
        if (currentZoom < 9) { setSelectedStation(null); return; }
        const point = [parseFloat(selectedStation.lon), parseFloat(selectedStation.lat)];
        const bounds = map.current.getBounds();
        if (bounds.contains(point)) {
            const pixel = map.current.project(point);
            const features = map.current.queryRenderedFeatures(
                [[pixel.x - 10, pixel.y - 10], [pixel.x + 10, pixel.y + 10]],
                { layers: ['unclustered-point'] }
            );
            const isRenderedAsPin = features.some(f => f.properties.id === selectedStation.id);
            if (!isRenderedAsPin) { setSelectedStation(null); }
        }
    };
    map.current.on('zoomend', checkVisibility);
    map.current.on('moveend', checkVisibility);
    return () => {
        if (map.current) {
            map.current.off('zoomend', checkVisibility);
            map.current.off('moveend', checkVisibility);
        }
    };
  }, [selectedStation, mapLoaded, setSelectedStation]);

  // --- SYNC DATA ---
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    const stationSource = map.current.getSource("stations");
    if (stationSource) {
      const geojson = {
        type: "FeatureCollection",
        features: stations.map((s) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [parseFloat(s.lon), parseFloat(s.lat)] },
          properties: { ...s },
        })),
      };
      stationSource.setData(geojson);
    }
    const radiusSource = map.current.getSource("radius-overlay");
    if (radiusSource) {
      if (searchedLocation && searchedLocation.lat && searchedLocation.lon) {
        const center = [searchedLocation.lon, searchedLocation.lat];
        radiusSource.setData(createCircleGeoJSON(center, radius) || { type: "FeatureCollection", features: [] });
      } else {
        radiusSource.setData({ type: "FeatureCollection", features: [] });
      }
    }
  }, [stations, searchedLocation, radius, mapLoaded]);

  // --- HIGHLIGHT SELECTED ---
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (selectedStation && selectedStation.lat && selectedStation.lon) {
      const coords = [parseFloat(selectedStation.lon), parseFloat(selectedStation.lat)];
      
      const isMobile = window.innerWidth < 900; 

      // ⬇️ REFINED FRAME: 
      // top: 50 -> Reserves just enough space for the popup at the top.
      // bottom: 0 -> Keeps the marker from being pushed up by imaginary panels.
      const flyToPadding = isMobile 
        ? { top: 110, bottom: 0, left: 10, right: 10 } 
        : { top: 0, bottom: 0, left: 0, right: 0 };

      map.current.flyTo({ 
          center: coords, 
          zoom: 15, 
          speed: 1.2, 
          essential: true,
          padding: flyToPadding // <--- Applied here
      });

      handleOpenPopup(selectedStation, coords);
      if (map.current.getLayer("unclustered-point")) {
        map.current.setLayoutProperty("unclustered-point", "icon-image", [
          "case", ["==", ["get", "id"], selectedStation.id], "custom-teardrop-active", "custom-teardrop"
        ]);
        map.current.setLayoutProperty("unclustered-point", "icon-size", [
          "case", ["==", ["get", "id"], selectedStation.id], 0.9, 0.7
        ]);
      }
    } else {
      if (map.current.getLayer("unclustered-point")) {
        map.current.setLayoutProperty("unclustered-point", "icon-image", "custom-teardrop");
        map.current.setLayoutProperty("unclustered-point", "icon-size", 0.7);
      }
      closePopup();
    }
  }, [selectedStation, mapLoaded]);

  // --- AUTO-ZOOM ON SEARCH ---
  useEffect(() => {
    if (!map.current || !searchedLocation) return;
    if(searchedLocation.lat && searchedLocation.lon) {
        const targetZoom = getZoomForRadius(radius);
        map.current.flyTo({
            center: [parseFloat(searchedLocation.lon), parseFloat(searchedLocation.lat)],
            zoom: targetZoom,
            essential: true
        });
        setSelectedStation(null);
    }
  }, [searchedLocation, radius, setSelectedStation]); 

  // ---  CENTER MARKER (PULSE) ---
  useEffect(() => {
    if (!map.current) return;
    if (userLocationMarker.current) {
        userLocationMarker.current.remove();
        userLocationMarker.current = null;
    }
    if (searchedLocation && searchedLocation.lat && searchedLocation.lon) {
        const el = document.createElement('div');
        el.className = 'pulse-container';
        el.innerHTML = `
            <div class="pulse-ring pulse-ring-1"></div>
            <div class="pulse-ring pulse-ring-2"></div>
            <div class="center-dot"></div>
        `;
        el.style.width = '50px';
        el.style.height = '50px';
        userLocationMarker.current = new maplibregl.Marker({
            element: el,
            anchor: 'center' 
        })
        .setLngLat([parseFloat(searchedLocation.lon), parseFloat(searchedLocation.lat)])
        .addTo(map.current);
    }
  }, [searchedLocation]);

  // --- POPUP HANDLER ---
  const handleOpenPopup = (station, coordinates) => {
    if (popupRef.current) popupRef.current.remove();
    const popupNode = document.createElement("div");
    popupNode.className = "react-popup-container";
    popupRef.current = new maplibregl.Popup({
        closeButton: false, maxWidth: "320px", anchor: "bottom", offset: [0, -55] 
    }).setLngLat(coordinates).setDOMContent(popupNode).addTo(map.current);
    setPopupContainer(popupNode);
    setPopupStation(station);
  };

  const closePopup = useCallback(() => {
    if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
    }
    setPopupContainer(null);
    setPopupStation(null);
    setSelectedStation(null);
  }, [setSelectedStation]);

  return { mapContainer, mapLoaded, popupContainer, popupStation, closePopup };
};

const getZoomForRadius = (r) => {
    if (r <= 5) return 13;
    if (r <= 20) return 11;
    if (r <= 50) return 10;
    if (r <= 100) return 9;
    return 8;
};

export default useStationMap;
